import {
  ButtonAction,
  DropdownAction,
  ButtonWithTooltipAction,
  FormButtonAction,
} from 'platform/components';
import {HStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {useResponsiveActions} from '../hooks/useResponsiveActions';
import type {EntityHeaderActionsProps} from '../types/EntityHeaderActionsProps';
import {ActionItemWrapper} from './ActionItemWrapper';

export function EntityHeaderActions(props: EntityHeaderActionsProps) {
  const {responsiveActions} = useResponsiveActions({
    actions: props.actions,
    maxVisibleActions: props.maxVisibleActions,
    dropdownPosition: props.dropdownPosition,
  });

  if (isNilOrEmpty(responsiveActions)) {
    return null;
  }

  return (
    <HStack
      spacing={2}
      align="center"
      justify="flex-end"
      data-testid={props['data-testid']}
      wrap={props.wrap}
    >
      {responsiveActions?.map((action, key) => {
        const testId = action['data-testid'] ?? `[${key}]`;

        return match(action)
          .with({type: 'button'}, (buttonAction) => (
            <ActionItemWrapper
              isFullWidth={props.shouldStretchActions}
              key={`buttonAction-[${buttonAction.title}]-${testId}`}
            >
              <ButtonAction
                data-testid={suffixTestId(testId, props)}
                {...buttonAction}
                isFullWidth={props.shouldStretchActions}
              />
            </ActionItemWrapper>
          ))
          .with({type: 'buttonWithTooltip'}, (tooltipAction) => (
            <ActionItemWrapper
              isFullWidth={props.shouldStretchActions}
              key={`buttonWithTooltipAction-[${tooltipAction.title}]-${testId}`}
            >
              <ButtonWithTooltipAction
                data-testid={suffixTestId(testId, props)}
                {...tooltipAction}
                isFullWidth={props.shouldStretchActions}
              />
            </ActionItemWrapper>
          ))
          .with(
            {type: Pattern.union('dropdown-button', 'dropdown-iconButton')},
            (dropdownAction) => (
              <DropdownAction
                key={`dropdownAction-${testId}`}
                data-testid={suffixTestId(testId, props)}
                {...dropdownAction}
              />
            )
          )
          .with({type: 'custom'}, (customAction) => (
            <ActionItemWrapper
              isFullWidth={props.shouldStretchActions}
              key={`customAction-${testId}`}
            >
              {customAction.component}
            </ActionItemWrapper>
          ))
          .with({type: 'form-button'}, (formButtonAction) => (
            <ActionItemWrapper
              isFullWidth={props.shouldStretchActions}
              key={`formButtonAction-[${formButtonAction.title}]-${testId}`}
            >
              <FormButtonAction
                data-testid={suffixTestId(testId, props)}
                {...formButtonAction}
                isFullWidth={props.shouldStretchActions}
              />
            </ActionItemWrapper>
          ))
          .exhaustive();
      })}
    </HStack>
  );
}
